import React, { Fragment, useEffect, useState, useRef } from 'react';
import Helmet from 'react-helmet';
import compose from 'lodash.flowright';
import qs from 'query-string';
import { Formik } from 'formik';

import useAuthenticated from '../../../../hooks/useAuthenticated';
import useSignupAlbumCreate from './hooks/useSignupAlbumCreate';
import { formatPhone, isValidPhone } from '../../../../util/phone';
import withGoogleRecaptchaProvider from '../../../../components/auth/hocs/withGoogleRecaptchaProvider';
import MarcomLayout from '../../../../components/global/MarcomLayout';
import useShutterbugRegistration, {
  REGISTRATION_STEPS,
} from '../Registration/useShutterbugRegistration';
import RegistrationForm from '../Registration/components/RegistrationForm';
import RegistrationPhoneVerify from '../Registration/components/RegistrationPhoneVerify';
import RegistrationEmailPinVerify from '../Registration/components/RegistrationEmailPinVerify';
import { isValidEmail } from '../../../../util/email';
import WaldoSpeechBubble from '../../../../components/global/WaldoSpeechBubble';
import LoginAuthModal from '../../../../components/auth/LoginAuthModal/modal';
import FullScreenLoader from '../../../../components/global/FullScreenLoader';

const SignupAlbumCreate = ({ history, location }) => {
  const authenticated = useAuthenticated();
  const [loadingAuthenticated, setLoadingAuthenticated] = useState(true);
  const loadingTimer = useRef();

  useEffect(() => {
    loadingTimer.current = setTimeout(() => {
      setLoadingAuthenticated(false);
    }, 500);

    return () => {
      if (loadingTimer.current) {
        clearTimeout(loadingTimer.current);
      }
    };
  }, []);

  const { eventType: defaultEventType } = qs.parse(location.search, {
    arrayFormat: 'bracket',
  });

  const { onSignupEventCreateReminderSet } = useSignupAlbumCreate();
  const {
    plan,
    step,
    loading,
    onPhoneVerificationRequest,
    onPinVerify,
    onPinCodeResend,
    onCheckout,
    onEmailRegistrationRequest,
    onEmailPinVerify,
    onEmailAuthenticationRequest,
    onEventSKUSelected,
    requireHumanInput,
    blockLogin,
  } = useShutterbugRegistration({ isSignupAlbumsFlow: true });

  useEffect(() => {
    if (authenticated) {
      // Call reminder create
      onSignupEventCreateReminderSet({ eventType: defaultEventType });
      history.replace({
        pathname: `/app/shutterbug/albums/create`,
        search: location.search,
      });
    }
  }, [authenticated]);

  const onSubmit = values => {
    if (step === REGISTRATION_STEPS.EVENT_SKU_SELECTION) {
      onEventSKUSelected(values);
    }

    if (
      step === REGISTRATION_STEPS.DETAILS &&
      isValidPhone(values.phone_number)
    ) {
      onPhoneVerificationRequest(values);
    } else if (step === REGISTRATION_STEPS.DETAILS) {
      onEmailRegistrationRequest(values);
    }

    if (step === REGISTRATION_STEPS.PIN_VERIFY) {
      onPinVerify(values);
    }

    if (step === REGISTRATION_STEPS.EMAIL_PIN_VERIFY) {
      onEmailPinVerify(values);
    }

    if (step === REGISTRATION_STEPS.CHECKOUT) {
      onCheckout(values);
    }
  };

  const onValidate = values => {
    const errors = {};

    switch (step) {
      case REGISTRATION_STEPS.DETAILS: {
        if (!isValidEmail(values.email_address)) {
          errors.email_address = 'Email address is not valid';
        }

        if (
          values.phone_number.replace('+1', '').length > 0 &&
          !isValidPhone(values.phone_number)
        ) {
          errors.phone_number = 'Phone number is not valid';
        }
        break;
      }
      default:
        break;
    }
    return errors;
  };

  if (!authenticated && loadingAuthenticated) {
    return <FullScreenLoader />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Create Album</title>
      </Helmet>
      <MarcomLayout.Wrapper title="Create your account" signupDisabled>
        <Formik
          onSubmit={onSubmit}
          initialValues={{
            first_name: '',
            last_name: '',
            email_address: '',
            email_address_exists: false,
            phone_number: '+1',
            code: '',
            terms_checked: true,

            // Checkout logic
            stripe_token: null,
            plan,
            plan_id: null,
            eventAddonItems: [],
            selectedEventAddonItemIds: [],
            eventItemId: '',
            force_allow_action: false,
          }}
          validate={onValidate}
          validateOnBlur={false}
          validateOnChange={false}
          render={({
            values,
            handleSubmit,
            handleChange,
            handleBlur,
            errors,
            touched,
          }) => {
            return (
              <div className="signup-album-create">
                {step === REGISTRATION_STEPS.DETAILS && (
                  <>
                    <WaldoSpeechBubble
                      className="w-100-i mx-0 mb-2"
                      position="right"
                    >
                      Let&apos;s get to know each other!
                    </WaldoSpeechBubble>
                    <RegistrationForm
                      values={values}
                      handleSubmit={handleSubmit}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      loading={loading}
                      requireHumanInput={requireHumanInput}
                      blockLogin={blockLogin}
                      hasLoginModalSupport
                    />
                  </>
                )}

                {step === REGISTRATION_STEPS.PIN_VERIFY && (
                  <>
                    <WaldoSpeechBubble
                      className="w-100-i mx-0 mb-2"
                      position="right"
                    >
                      Please enter the code sent to:{' '}
                      <span className="text-primary">
                        {formatPhone(values.phone_number)}!
                      </span>
                    </WaldoSpeechBubble>
                    <RegistrationPhoneVerify
                      loading={loading}
                      className="signup-album-create-pin-step"
                      handleSubmit={handleSubmit}
                      handleChange={handleChange}
                      onPinCodeResend={onPinCodeResend}
                      onEmailSignup={() => onEmailRegistrationRequest(values)}
                      values={values}
                      requireHumanInput={requireHumanInput}
                      blockLogin={blockLogin}
                    />
                  </>
                )}
                {step === REGISTRATION_STEPS.EMAIL_PIN_VERIFY && (
                  <>
                    <WaldoSpeechBubble
                      className="w-100-i mx-0 mb-2"
                      position="right"
                    >
                      Please enter the code sent to:{' '}
                      <span className="text-primary">
                        {values.email_address}
                      </span>
                    </WaldoSpeechBubble>
                    <RegistrationEmailPinVerify
                      loading={loading}
                      handleSubmit={handleSubmit}
                      handleChange={handleChange}
                      className="signup-album-create-email-pin-step"
                      onPinCodeResend={() =>
                        onEmailAuthenticationRequest({
                          emailAddress: values.email_address,
                        })
                      }
                      values={values}
                    />
                  </>
                )}
              </div>
            );
          }}
        />
        <LoginAuthModal />
      </MarcomLayout.Wrapper>
    </Fragment>
  );
};

export default compose(withGoogleRecaptchaProvider)(SignupAlbumCreate);
